import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, FolderOpen, Lock, ShieldCheck, FlaskConical } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";

// Tiga kolom dokumen. Urutannya urutan spec, bukan alfabetis — sama dengan `urutan` 1..12 di tabel.
const KOLOM = [
  { kode: "administrasi", judul: "ADMINISTRASI", icon: FolderOpen },
  { kode: "uji_akurasi", judul: "UJI AKURASI", icon: FlaskConical },
  { kode: "laporan", judul: "LAPORAN", icon: FileText },
];

const ORANYE = "#F59E0B";
const HIJAU = "#10B981";

// Cermin dari `tautanSah` di node-backend/dokumen.js. Server tetap validasi sendiri — ini cuma
// supaya tombol simpan bisa mati sebelum permintaan dikirim.
const tautanSah = (s) => {
  if (typeof s !== "string") return false;
  const t = s.trim();
  if (!t || t.length > 500) return false;
  try {
    const u = new URL(t);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

// Satu fakta turunan, dipakai badge kolom dan server: selesai = terkunci DAN bertautan.
// URL adalah buktinya, jadi baris terkunci tanpa URL tidak dihitung selesai.
const selesai = (d) => d.terkunci && !!d.url;

function Kartu({ d, isAdmin, onSimpan }) {
  // Checkbox TIDAK menyimpan state sendiri. Ia mencerminkan server dan hanya membuka field URL;
  // yang menulis ke server cuma tombol simpan. Kalau kotaknya membalik tampilannya sendiri,
  // admin akan melihat kotak tercentang dengan URL kosong — state yang memang tidak boleh ada.
  const [url, setUrl] = useState("");
  const [sibuk, setSibuk] = useState(false);
  const terkunci = d.terkunci;
  // Spec Rule 3: terkunci penuh, ATAU sudah ada URL (hasil suntingan langsung DB) — dua-duanya
  // tidak bisa dicentang dari UI. Baris seperti itu diperbaiki lewat database, bukan di sini.
  const bisaDicentang = isAdmin && !terkunci && !d.url;
  const s = selesai(d);
  const [buka, setBuka] = useState(false);
  const tampilInput = bisaDicentang && buka;

  const simpan = async () => {
    setSibuk(true);
    try {
      await onSimpan(d.id, url);
      toast.success("Dokumen dikunci");
      setBuka(false);
      setUrl("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSibuk(false);
    }
  };

  return (
    <div
      data-testid={`doc-card-${d.kode}`}
      className={`rounded-md border p-3 transition-colors duration-200 ${
        terkunci ? "border-border/60 bg-[#0F1622] opacity-80" : "border-border bg-[#131B29] hover:border-sky-500/40"
      }`}
    >
      <div className="flex items-start gap-2.5">
        <Checkbox
          data-testid={`doc-check-${d.kode}`}
          checked={s || tampilInput}
          disabled={!bisaDicentang}
          onCheckedChange={(v) => { setBuka(!!v); if (!v) setUrl(""); }}
          aria-label={`Tandai selesai: ${d.nama}`}
          className="mt-0.5"
        />
        <p className={`min-w-0 flex-1 truncate text-xs font-medium ${terkunci ? "text-slate-400" : "text-slate-200"}`} title={d.nama}>
          {d.nama}
        </p>
        <StatusBadge
          status={s ? "Selesai" : "Belum"}
          color={s ? HIJAU : ORANYE}
          testId={`doc-status-${d.kode}`}
        />
        {terkunci ? (
          <Lock className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-label="Terkunci" />
        ) : null}
      </div>

      {tampilInput && (
        <div className="mt-2.5 flex gap-2">
          <Input
            data-testid={`doc-url-${d.kode}`}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
            className="h-8 text-xs"
            aria-label={`Tautan dokumen ${d.nama}`}
          />
          <Button
            data-testid={`doc-save-${d.kode}`}
            size="sm"
            disabled={!tautanSah(url) || sibuk}
            onClick={simpan}
            className="h-8 shrink-0 px-3 text-xs"
          >
            Simpan
          </Button>
        </div>
      )}

      {terkunci && d.url && (
        <a
          href={d.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`doc-link-${d.kode}`}
          className="mt-2 block truncate font-mono text-[11px] text-sky-400 hover:text-sky-300"
        >
          {d.url}
        </a>
      )}
    </div>
  );
}

function Kolom({ kolom, daftar, isAdmin, onSimpan }) {
  const selesaiN = daftar.filter(selesai).length;
  const Icon = kolom.icon;
  return (
    <section
      data-testid={`doc-column-${kolom.kode}`}
      className="flex min-w-0 flex-col rounded-lg border border-border bg-[#0D131E] p-3"
    >
      <header className="mb-3 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-500/15 text-sky-300">
            <Icon className="h-3.5 w-3.5" />
          </span>
          <h3 className="truncate font-mono text-[11px] uppercase tracking-widest text-slate-300">{kolom.judul}</h3>
        </div>
        <span
          data-testid={`doc-badge-${kolom.kode}`}
          className="shrink-0 rounded-md bg-sky-500/15 px-2 py-0.5 font-mono text-[11px] font-semibold text-sky-300"
        >
          {selesaiN}/{daftar.length}
        </span>
      </header>
      <div className="flex flex-col gap-2">
        {daftar.map((d) => (
          <Kartu key={d.id} d={d} isAdmin={isAdmin} onSimpan={onSimpan} />
        ))}
      </div>
    </section>
  );
}

export default function DocumentProgressPage() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pola rumah (EntityPage): kirim → refetch dari server. `terkunci` dan `url` di layar selalu
  // berasal dari respons terakhir, bukan tebakan lokal — penting karena kunci itu permanen.
  const load = () =>
    api.get("/dokumen-progress").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSimpan = async (id, url) => {
    try {
      await api.post(`/dokumen-progress/${id}/selesaikan`, { url });
    } finally {
      // Selalu refetch, termasuk saat 409: admin lain mungkin sudah mengunci baris ini lebih dulu,
      // dan tanpa refetch kita duduk di form yang selamanya bisa dicoba lagi.
      load();
    }
  };

  if (loading) return <div className="p-10 text-slate-500" data-testid="doc-loading">Memuat dokumen...</div>;

  const semua = rows.reduce((a, d) => a + (selesai(d) ? 1 : 0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-10 grid-bg min-h-screen" data-testid="doc-page">
      <div className="rise">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sky-400">Modul 05 · Document Progress</p>
        <div className="mt-1 flex flex-wrap items-end justify-between gap-3">
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold" data-testid="doc-page-title">Dokumen Progres</h2>
          <div className="rounded-md border border-border bg-[#131B29] px-5 py-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Dokumen Selesai</p>
            <p className="font-display text-3xl font-extrabold text-emerald-400" data-testid="doc-total">{semua}/{rows.length}</p>
          </div>
        </div>
        <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-400">
          <ShieldCheck className="h-4 w-4 text-sky-400" />
          Dokumen yang sudah disimpan dengan tautan <strong className="text-slate-300">terkunci permanen</strong> — periksa tautannya sebelum menyimpan.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {KOLOM.map((k) => (
          <Kolom key={k.kode} kolom={k} daftar={rows.filter((d) => d.komponen === k.kode)} isAdmin={isAdmin} onSimpan={onSimpan} />
        ))}
      </div>
    </div>
  );
}
